import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Search, UserPlus, X } from "@fg/icons";
import { connectParityApi, normalizeUserId, type ChatListItem } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { cn } from "@/lib/utils";

interface Person {
    userId: string;
    email: string;
    displayName: string | null;
}

/**
 * Teams' "Add people" (FPT-8: adding people to a chat lives in Meeting only).
 * In a group chat the people join it and see its history. A 1:1 chat stays
 * private: adding someone starts a new group chat with the other person and
 * the people picked, and opens it.
 */
export function AddPeopleDialog({ chat, memberIds, onClose }: {
    chat: ChatListItem;
    /** Everyone already in the chat (normalised ids), never offered again. */
    memberIds: string[];
    onClose: () => void;
}) {
    const isDm = chat.kind === "dm";
    const token = useAccessToken();
    const qc = useQueryClient();
    const navigate = useNavigate();
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<Person[]>([]);
    const [picked, setPicked] = useState<Person[]>([]);
    const [searching, setSearching] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const q = query.trim();
        if (q.length < 2) {
            setResults([]);
            return;
        }
        let cancelled = false;
        setSearching(true);
        const handle = window.setTimeout(async () => {
            try {
                const r = await connectParityApi.searchDirectory(q, token);
                if (!cancelled) setResults(r.filter((u) => !memberIds.includes(normalizeUserId(u.userId))));
            } catch (e) {
                if (!cancelled) setError(e instanceof Error ? e.message : String(e));
            } finally {
                if (!cancelled) setSearching(false);
            }
        }, 200);
        return () => {
            cancelled = true;
            window.clearTimeout(handle);
        };
    }, [query, token, memberIds]);

    const toggle = (u: Person) =>
        setPicked((prev) => (prev.some((p) => p.userId === u.userId) ? prev.filter((p) => p.userId !== u.userId) : [...prev, u]));

    const submit = async () => {
        if (picked.length === 0 || saving) return;
        setSaving(true);
        setError(null);
        try {
            if (isDm) {
                const peer = chat.peerUserId && chat.peerEmail
                    ? [{ userId: chat.peerUserId, email: chat.peerEmail, displayName: chat.title }]
                    : [];
                const group = await connectParityApi.createConversation({
                    participants: [...peer, ...picked.map((p) => ({ userId: p.userId, email: p.email, displayName: p.displayName ?? undefined }))]
                }, token);
                await qc.invalidateQueries({ queryKey: ["chats"] });
                onClose();
                navigate(`/?view=dms&chat=group:${encodeURIComponent(group.id)}`);
            } else {
                await connectParityApi.addConversationMembers(chat.id, picked.map((p) => p.userId), token);
                await Promise.all([
                    qc.invalidateQueries({ queryKey: ["conversation-members", chat.id] }),
                    qc.invalidateQueries({ queryKey: ["chats"] })
                ]);
                onClose();
            }
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Add people"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
            onKeyDown={(e) => { if (e.key === "Escape" && !saving) onClose(); }}
        >
            <div className="flex w-full max-w-md flex-col rounded-lg border bg-card p-4 shadow-2xl">
                <div className="mb-2 flex items-center justify-between">
                    <h2 className="text-base font-semibold">Add people</h2>
                    <button type="button" onClick={onClose} aria-label="Close" disabled={saving} className="rounded p-1 hover:bg-accent disabled:opacity-50">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <p className="mb-3 text-xs text-muted-foreground">
                    {isDm
                        ? `Starts a new group chat with ${chat.title} and the people you add. Your chat with ${chat.title} stays private.`
                        : "They'll see the chat's history."}
                </p>

                <div className="relative mb-2">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                        autoFocus
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Type a name or email"
                        aria-label="Search people"
                        className="w-full rounded-md border bg-background py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                </div>

                {picked.length > 0 ? (
                    <div className="mb-2 flex flex-wrap gap-1">
                        {picked.map((p) => (
                            <span key={p.userId} className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
                                {p.displayName ?? p.email}
                                <button type="button" onClick={() => toggle(p)} aria-label={`Remove ${p.displayName ?? p.email}`} className="rounded-full hover:bg-primary/20">
                                    <X className="h-3 w-3" />
                                </button>
                            </span>
                        ))}
                    </div>
                ) : null}

                <div className="max-h-56 overflow-auto rounded-md border">
                    {searching ? (
                        <p className="flex items-center gap-2 p-3 text-xs text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
                        </p>
                    ) : results.length === 0 ? (
                        <p className="p-3 text-xs text-muted-foreground">
                            {query.trim().length >= 2 ? "No one else matches." : "Start typing to search the directory."}
                        </p>
                    ) : (
                        <ul role="list" className="divide-y">
                            {results.map((u) => {
                                const selected = picked.some((p) => p.userId === u.userId);
                                return (
                                    <li key={u.userId}>
                                        <button
                                            type="button"
                                            onClick={() => toggle(u)}
                                            aria-pressed={selected}
                                            className={cn("flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent", selected && "bg-primary/5")}
                                        >
                                            <input type="checkbox" checked={selected} readOnly tabIndex={-1} className="h-3.5 w-3.5" />
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate font-medium">{u.displayName ?? u.email}</span>
                                                {u.displayName ? <span className="block truncate text-xs text-muted-foreground">{u.email}</span> : null}
                                            </span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                {error ? (
                    <p className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive" role="alert">
                        {error}
                    </p>
                ) : null}

                <div className="mt-4 flex justify-end gap-2">
                    <button type="button" onClick={onClose} disabled={saving} className="rounded-md border px-3 py-1.5 text-sm hover:bg-accent disabled:opacity-50">
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => void submit()}
                        disabled={picked.length === 0 || saving}
                        className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    >
                        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
                        {isDm ? "Start group chat" : `Add${picked.length > 0 ? ` (${picked.length})` : ""}`}
                    </button>
                </div>
            </div>
        </div>
    );
}
